import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { createProject, updateProject } from '../../../services/api'
import type { Project } from '../../../types/database'

interface ProjectFormProps {
  project?: Project | null
  onSave: () => void
  onCancel: () => void
}

export default function ProjectForm({ project, onSave, onCancel }: ProjectFormProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    long_description: '',
    tags: [] as string[],
    image_url: '',
    project_url: '',
    github_url: '',
    featured: true,
    order: 0,
  })
  const [tagInput, setTagInput] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        description: project.description || '',
        long_description: project.long_description || '',
        tags: project.tags || [],
        image_url: project.image_url || '',
        project_url: project.project_url || '',
        github_url: project.github_url || '',
        featured: project.featured ?? true,
        order: project.order || 0,
      })
    }
  }, [project])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (project) {
        await updateProject(project.id, formData)
      } else {
        await createProject(formData as Omit<Project, 'id' | 'created_at' | 'updated_at'>)
      }
      onSave()
    } catch (error) {
      console.error('Error saving project:', error)
    } finally {
      setLoading(false)
    }
  }

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      })
      setTagInput('')
    }
  }

  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((t) => t !== tag),
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="neu rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar p-4 sm:p-8 m-4 sm:m-0"
      >
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-text-primary">
            {project ? 'Edit Project' : 'New Project'}
          </h2>
          <button
            onClick={onCancel}
            className="text-text-primary/70 hover:text-text-primary text-xl sm:text-2xl transition-colors touch-manipulation p-2 -mr-2"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
              placeholder="Project Title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              required
              rows={3}
              className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all resize-none"
              placeholder="Short description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
              Long Description
            </label>
            <textarea
              value={formData.long_description}
              onChange={(e) =>
                setFormData({ ...formData, long_description: e.target.value })
              }
              rows={5}
              className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all resize-none"
              placeholder="Detailed description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Image URL
              </label>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) =>
                  setFormData({ ...formData, image_url: e.target.value })
                }
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Order
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) =>
                  setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
                }
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Project URL
              </label>
              <input
                type="url"
                value={formData.project_url}
                onChange={(e) =>
                  setFormData({ ...formData, project_url: e.target.value })
                }
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                GitHub URL
              </label>
              <input
                type="url"
                value={formData.github_url}
                onChange={(e) =>
                  setFormData({ ...formData, github_url: e.target.value })
                }
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="https://github.com/..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
              Tags
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addTag()
                  }
                }}
                className="flex-1 px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                placeholder="Add a tag and press Enter"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2.5 sm:py-2 neu rounded-xl text-accent hover:text-accent-secondary transition-colors neu-hover touch-manipulation text-sm sm:text-base"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-2 px-3 py-1 neu rounded-full text-sm text-accent"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:text-primary-300"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-text-primary/70">
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) =>
                  setFormData({ ...formData, featured: e.target.checked })
                }
                className="w-4 h-4 rounded border-text-primary/20 neu text-accent focus:ring-accent"
              />
              <span>Featured</span>
            </label>
          </div>

          <div className="flex gap-4 pt-4">
            <motion.button
              type="submit"
              disabled={loading}
              className="flex-1 px-5 sm:px-6 py-3 neu rounded-xl font-medium text-text-primary neu-hover disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation text-sm sm:text-base"
              whileHover={loading ? {} : { scale: 1.02, y: -1 }}
              whileTap={loading ? {} : { scale: 0.98 }}
              transition={{ type: "spring", stiffness: 60, damping: 10 }}
            >
              {loading ? 'Saving...' : project ? 'Update Project' : 'Create Project'}
            </motion.button>
            <motion.button
              type="button"
              onClick={onCancel}
              className="px-5 sm:px-6 py-3 neu rounded-xl font-medium text-text-primary/70 hover:text-text-primary transition-colors neu-hover touch-manipulation text-sm sm:text-base"
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 60, damping: 10 }}
            >
              Cancel
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}


